// Web Haptics feedback for ship events, built on the `web-haptics` library
// (https://github.com/lochie/web-haptics): navigator.vibrate patterns on
// Android Chrome, the iOS switch-control trick elsewhere. iOS notes:
// - iOS 17.4–26.4: the library's programmatic switch toggles fire system
//   ticks from the sim loop.
// - iOS 26.5+: Apple narrowed the switch haptic to REAL direct taps, so
//   sim-loop haptics go silent — the direct-tap overlays below
//   (makeHapticSwitch, laid over boon cards / pause / settings TEST) are
//   the only buzzes on those versions, and they always work.
// `enabled` gates all playback; intensity scales pattern strength.

import { WebHaptics } from 'web-haptics';

export interface HapticsSettings {
  enabled: boolean;
  intensity: number; // 0..1
}

/** iOS/iPadOS detection (Safari has no navigator.vibrate). */
function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (
    /iP(hone|ad|od)/.test(navigator.userAgent) ||
    // iPadOS 13+ reports as Macintosh; touch gives it away.
    (navigator.platform === 'MacIntel' && (navigator.maxTouchPoints ?? 0) > 1)
  );
}

/**
 * A native iOS switch control styled as an invisible overlay: lay it over a
 * real control (button, card) and a genuine tap toggles it — firing the
 * system haptic tick with NO JavaScript on the haptic path (iOS 26.5-safe).
 * Rendered at full size with native appearance (opacity only hides it;
 * display:none or a stripped control would disable the haptic). The tap
 * still reaches the control's own handler via the wrapper's listener.
 */
export function makeHapticSwitch(): HTMLInputElement {
  const el = document.createElement('input');
  el.type = 'checkbox';
  el.setAttribute('switch', '');
  el.setAttribute('aria-hidden', 'true');
  el.tabIndex = -1;
  el.className = 'la-haptic-tick';
  return el;
}

export class Haptics {
  private enabled: boolean;
  private intensity = 1;
  private readonly engine: 'vibrate' | 'ios-switch' | 'none';
  private readonly lib: WebHaptics | null;

  constructor() {
    const supported =
      typeof navigator !== 'undefined' &&
      (WebHaptics.isSupported || isIOS()) &&
      (navigator.maxTouchPoints ?? 0) > 0;
    this.engine = !supported
      ? 'none'
      : WebHaptics.isSupported
        ? 'vibrate'
        : 'ios-switch';
    this.lib = supported ? new WebHaptics() : null;
    this.enabled = supported;
  }

  /** Engine id for settings UI ('vibrate' | 'ios-switch' | 'none'). */
  get engineName(): string {
    return this.engine;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled && this.engine !== 'none';
  }

  setIntensity(intensity: number): void {
    this.intensity = Math.min(1, Math.max(0, intensity));
  }

  /** Play a web-haptics preset name or custom pattern. */
  play(input: string | number | readonly number[]): void {
    if (!this.enabled || !this.lib) return;
    const pattern = Array.isArray(input) ? [...(input as readonly number[])] : input;
    void this.lib.trigger(pattern as string | number | number[], {
      intensity: this.intensity,
    });
  }

  /** Clean up the library's DOM/audio resources. */
  destroy(): void {
    this.lib?.destroy();
  }
}

// Ship-event haptics mapped onto the web-haptics preset vocabulary
// (calibrated patterns; see the library's defaultPatterns).
export const HAPTIC_PATTERNS = {
  playerHit: 'heavy',
  dash: 'medium',
  kill: 'light',
  pickup: 'selection',
  levelUp: 'success',
  gameOver: 'error',
  waveStart: 'rigid',
  // Extended set: every run beat worth feeling.
  levelupOffer: 'medium',
  waveClear: 'soft',
  bossSpawn: 'warning',
  bossDefeated: 'success',
  lootCrate: 'nudge',
  nova: 'buzz',
  captainSkill: 'selection',
  wormhole: 'nudge',
  stormStart: 'warning',
  hazardImpact: 'rigid',
} as const;
